<template>
  <div>
    <v-row class="d-flex" no-gutters>
      <v-col class="col-md-4 col-lg-4 col-sm-12">
        <m-select
          v-model="filters"
          :clearable="true"
          :items="filterListRef"
          :multiple="true"
          :placeholder="$t('offer-manager.offers-list.lbl-filter-status')"
          :showSelectedCounter="true"
          data-cy="offerStatusFilter"
          id="offer-status-filter">
          <template v-slot:prepend-inner>
            <i class="icon-mapit icon-filter-status"></i>
          </template>
        </m-select>
      </v-col>
      <v-btn class="px-0 px-lg-6 ml-3 text-center no-shadow aux-btn" :outlined="true" @click="refetch()">
        <v-icon class="mr-lg-2 fa-light fa-arrows-repeat" />
        <span class="btn-text">{{ $t('honda.registration.list.btn_refresh') }}</span>
      </v-btn>
      <v-spacer />
      <v-col class="col-md-4 col-lg-4 col-sm-12">
        <v-row no-gutters>
          <m-text-field
            v-model.trim="text"
            :clearable="true"
            :placeholder="$t('honda.registration.list.placeholder_search')"
            data-cy="registrationsSearchFilter"
            class="flex-grow-1 flex-shrink-1"
            @keyup="onTextKeyup"
            @keydown="onTextKeydown" />
        </v-row>
      </v-col>
    </v-row>
    <v-data-table
      id="registrations-table-list"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'fas fa-angle-double-left',
        lastIcon: 'fas fa-angle-double-right',
        prevIcon: 'fas fa-angle-left',
        nextIcon: 'fas fa-angle-right',
        'items-per-page-text': $t('honda.registration.list.footer_num_x_page'),
        'items-per-page-options': [10, 25, 50],
      }"
      :headers="headers"
      :items="registrationsList"
      :item-class="getRowClass"
      :items-per-page="10"
      :server-items-length="total"
      :loading="loading || registrationListLoading"
      :loading-text="$t('honda.registration.list.loading')"
      :no-data-text="
        $t(
          registrationsList && registrationsList.length === 0 && text
            ? 'honda.registration.list.no_data'
            : 'honda.registration.list.no_data_search',
          {search: text},
        )
      "
      :no-results-text="$t('offer-manager.offers-list.no-data-search')"
      class="elevation-0 pt-4 registrations-table-list"
      sort-by="offerDate"
      :page.sync="currentPage"
      @update:page="onPageChange"
      @update:items-per-page="onItemsPerPageChange"
      @update:sort-by="onSortByChange"
      @update:sort-desc="onSortDescChange">
      <template #footer.page-text="{pageStart, pageStop, itemsLength}">
        {{ pageStart }}-{{ pageStop }} {{ $t('honda.registration.list.of') }} {{ itemsLength }}
      </template>

      <template v-slot:item.star="{item}">
        <v-icon class="icon-mapit no-padding-right" :color="isRecent(item) ? 'warning' : 'transparent'">|</v-icon>
      </template>

      <template v-slot:item.createdAt="{item}">
        <span class="lbl-registration-bold">{{ item.createdAt | formatDate }}</span>
      </template>

      <template v-slot:item.vehicle.model="{item}">
        <span class="lbl-registration-bold lbl-bold">{{ item.vehicle.model }}</span>
      </template>

      <template v-slot:item.vehicle.registrationNumber="{item}">
        <span class="lbl-registration-bold lbl-bold">{{ item.vehicle.registrationNumber }}</span>
      </template>

      <template v-slot:item.account.firstName="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{on, attrs}">
            <div
              v-bind="attrs"
              v-on="(getFullName(item).length >= 20 || (item.account.email && item.account.email.length >= 30)) && on"
              class="customer py-4 lbl-registration-bold lbl-bold">
              {{ trimName(getFullName(item), 20) }}<br />
              <span class="customer-email">{{
                item.account.email ? trimName(item.account.email, 30) : getDocumentNumber(item)
              }}</span
              ><br />
              <span class="customer-phone">{{ item.account.phone | formatPhone }}</span>
            </div>
          </template>
          <span>{{ getFullName(item) }}<br />{{ item.account.email || getDocumentNumber(item) }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.products="{item}">
        <v-tooltip bottom v-for="pc in getProductChips(item.products)" :key="pc.value">
          <template v-slot:activator="{on, attrs}">
            <v-chip
              class="ma-1 service-chip lbl-registration-bold"
              color="#fff"
              text-color="#222"
              small
              label
              v-bind="attrs"
              v-on="on"
              >{{ pc.value }}
            </v-chip>
          </template>
          <span>{{ pc.tooltip }}</span>
        </v-tooltip>
      </template>

      <template v-slot:item.contractsSigned="{item}">
        <v-icon class="fa-solid fa-minus m-auto" v-if="!hasHondaPlusOrInsurance(item)"></v-icon>
        <v-tooltip bottom v-else-if="item.contractsSigned">
          <template v-slot:activator="{on, attrs}">
            <v-icon color="secondary" class="fa-solid fa-square-check" v-bind="attrs" v-on="on" />
          </template>
          <span>{{ $t('honda.registration.list.tooltip_sign_correct') }}</span>
        </v-tooltip>
        <v-tooltip bottom v-else>
          <template v-slot:activator="{on, attrs}">
            <v-icon color="primary" class="fa-solid fa-square-xmark" v-bind="attrs" v-on="on" />
          </template>
          <span>{{ $t('honda.registration.list.tooltip_sign_incorrect') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.accountRegistrationCompleted="{item}">
        <v-icon class="fa-solid fa-minus m-auto" v-if="!hasHondaMapit(item)"></v-icon>
        <v-tooltip bottom v-else-if="item.accountRegistrationCompleted">
          <template v-slot:activator="{on, attrs}">
            <v-icon color="secondary" class="fa-solid fa-square-check" v-bind="attrs" v-on="on" />
          </template>
          <span>{{ $t('honda.registration.list.tooltip_register_correct') }}</span>
        </v-tooltip>
        <v-tooltip bottom v-else>
          <template v-slot:activator="{on, attrs}">
            <v-icon color="primary" class="fa-solid fa-square-xmark" v-bind="attrs" v-on="on" />
          </template>
          <span>{{ $t('honda.registration.list.tooltip_register_incorrect') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.hasComms="{item}">
        <v-icon
          class="fa-solid fa-minus m-auto no-padding-left"
          v-if="!hasHondaMapit(item) || item.deviceHealthChecked === undefined"></v-icon>
        <v-tooltip bottom v-else-if="hasComms(item)" class="no-padding-left">
          <template v-slot:activator="{on, attrs}">
            <v-icon color="secondary" class="fa-solid fa-wifi" :size="20" v-bind="attrs" v-on="on" />
          </template>
          <span>{{ $t('honda.registration.list.tooltip_comms_correct') }}</span>
        </v-tooltip>
        <v-tooltip bottom v-else class="no-padding-left">
          <template v-slot:activator="{on, attrs}">
            <v-icon color="primary" class="fa-solid fa-wifi-slash" :size="20" v-bind="attrs" v-on="on" />
          </template>
          <span>{{ $t('honda.registration.list.tooltip_comms_incorrect') }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.actions="{item}">
        <v-menu transition="slide-x-transition" bottom right>
          <template v-slot:activator="{on, attrs}">
            <div v-bind="attrs" v-on="on">
              <v-icon color="#222222" class="icon-mapit notranslatable" :size="28" :data-cy="`menu-${item.id}`"
                >)
              </v-icon>
            </div>
          </template>

          <v-list v-if="!item.archived" dense class="list-menu-actions">
            <v-list-item :key="generateUUID()" @click.once="confirmFinish(item)">
              <v-list-item-title>
                {{ $t('honda.registration.list.finish_registration') }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              :key="generateUUID()"
              @click.once="sendEmailRegisterApp(item)"
              :disabled="
                loading ||
                !item.account.email ||
                !hasHondaMapit(item) ||
                (hasHondaMapit(item) && item.accountRegistrationCompleted)
              ">
              <v-list-item-title>{{ $t('honda.registration.list.btn_send_email_register') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              :key="generateUUID()"
              @click.once="sendEmailSignDocs(item)"
              :disabled="
                loading ||
                !item.account.email ||
                !hasHondaPlusOrInsurance(item) ||
                (hasHondaPlusOrInsurance(item) && item.contractsSigned)
              ">
              <v-list-item-title>{{ $t('honda.registration.list.btn_send_docs') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              :key="generateUUID()"
              @click.once="getRegistrationContracts(item)"
              :disabled="
                loading || !hasHondaPlusOrInsurance(item) || (hasHondaPlusOrInsurance(item) && item.contractsSigned)
              ">
              <v-list-item-title>{{ $t('honda.registration.list.btn_download_docs') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              :key="generateUUID()"
              @click.once="markDocsSigned(item)"
              :disabled="
                loading || !hasHondaPlusOrInsurance(item) || (hasHondaPlusOrInsurance(item) && item.contractsSigned)
              ">
              <v-list-item-title>{{ $t('honda.registration.list.btn_check_docs_signed') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              :key="generateUUID()"
              @click.once="confirmDeleteRegistration(item)"
              :disabled="item.contractsSigned || item.locked">
              <v-list-item-title>{{ $t('honda.registration.list.btn_delete_registration') }}</v-list-item-title>
            </v-list-item>
          </v-list>
          <v-list v-else dense class="list-menu-actions">
            <v-list-item
              :key="generateUUID()"
              @click.once="confirmUnarchive(item)"
              :disabled="loading || item.locked || item.resolutionStatus === 'FINISHED'">
              <v-list-item-title>{{ $t('honda.registration.list.unarchive_registration') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <mapit-dialog :dialog="dialogResult" :key="`dialog-result-${resultStringPopup}`" @close="openCloseDialogResult()">
      <template v-slot:header-icon>
        <v-icon class="text-secondary" v-if="resultStringPopup !== 'error'">fa fa-check</v-icon>
        <v-icon class="text-orange" v-else>fa fa-exclamation-triangle</v-icon>
      </template>
      <template v-slot:body>
        <div class="dialog-subtitle" v-balance-text>
          {{ $t(`honda.registration.list.popup.${resultStringPopup}`) }}
        </div>
      </template>
      <template v-slot:footer-center>
        <v-btn class="btn-primary" @click="openCloseDialogResult()">{{ $t('buttons.next') }} </v-btn>
      </template>
    </mapit-dialog>
    <mapit-dialog :dialog="dialogArchived" @close="openCloseDialogArchive()">
      <template v-slot:header-icon>
        <v-icon class="text-secondary">fa fa-check</v-icon>
      </template>
      <template v-slot:header-text>
        <div class="text-center" v-balance-text>{{ $t(`honda.registration.list.popup.unarchive.title`) }}</div>
      </template>
      <template v-slot:body>
        <div class="text-center" v-balance-text>
          {{ $t(`honda.registration.list.popup.unarchive.body`) }}
        </div>
      </template>
      <template v-slot:footer-center>
        <v-btn
          :loading="loadingFinishRegistration"
          :disabled="loadingFinishRegistration"
          class="btn-primary"
          :key="generateUUID()"
          @click.once="unarchiveRegistration(itemToUnarchive)"
          >{{ $t('buttons.accept') }}
        </v-btn>
      </template>
    </mapit-dialog>

    <mapit-dialog :dialog="dialogConfirmDelete" @close="openCloseDialogConfirmDelete()">
      <template v-slot:header-icon>
        <v-icon class="text-orange">fa fa-exclamation-triangle</v-icon>
      </template>
      <template v-slot:header-text>
        <div class="text-center" v-balance-text>{{ $t(`honda.registration.list.popup.confirm_delete.title`) }}</div>
      </template>
      <template v-slot:body>
        <div
          class="text-center"
          v-balance-text.children
          v-html="$t(`honda.registration.list.popup.confirm_delete.body`)" />
      </template>
      <template v-slot:footer-center>
        <v-btn class="btn-gray-darken" @click="openCloseDialogConfirmDelete()">{{ $t('buttons.cancel') }} </v-btn>
        <v-btn
          class="btn-primary"
          :key="generateUUID()"
          @click.once="deleteRegistration()"
          :loading="loadingDeletingRegistration"
          >{{ $t('buttons.confirm-delete') }}
        </v-btn>
      </template>
    </mapit-dialog>

    <mapit-dialog :dialog="dialogConfirm" @close="openCloseDialogConfirm()">
      <template v-slot:header-icon>
        <v-icon class="text-secondary">fa fa-check</v-icon>
      </template>
      <template v-slot:header-text>
        <div class="text-center" v-balance-text>{{ $t(`honda.registration.list.popup.confirm.title`) }}</div>
      </template>
      <template v-slot:body>
        <div class="text-center" v-balance-text>
          {{ $t(`honda.registration.list.popup.confirm.body`) }}
        </div>
      </template>
      <template v-slot:footer-center>
        <v-btn
          :loading="loadingFinishRegistration"
          :disabled="loadingFinishRegistration"
          class="btn-primary"
          :key="generateUUID()"
          @click.once="finishRegistration(selectedToFinish)"
          >{{ $t('buttons.accept') }}
        </v-btn>
      </template>
    </mapit-dialog>
  </div>
</template>
<script lang="ts">
import {computed, defineComponent, PropType, Ref, ref, watch} from '@vue/composition-api'
import moment from 'moment/moment'

import coreApi from '@/api/core.api.js'
import registrationsApi from '@/api/registrations.api'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import {useTableList} from '@/composables/table-list'
import {downloadSignedUrlFiles} from '@/helpers/downloadFile.js'
import {RegistrationArchivedFilters, RegistrationDoc, RegistrationFilters} from '@/types'
import {Product, RegistrationResolutionStatus} from '@/types/dynamodb-types'
import {mapGetters} from '@/utils/map-store'
import {generateUUID} from '@/utils/utils'

export default defineComponent({
  name: 'RegistrationsTable',
  components: {MapitDialog},
  props: {
    filterList: {
      type: Array as PropType<Array<RegistrationFilters | RegistrationArchivedFilters>>,
      required: true,
    },
    defaultFilters: {
      type: Array as PropType<Array<RegistrationFilters | RegistrationArchivedFilters>>,
      required: false,
      default: () => [],
    },
  },
  setup(props, {root}) {
    const {getGroup: group, getLocale: locale} = mapGetters(root.$store.getters)

    const filters = ref(props.defaultFilters) as Ref<Array<RegistrationFilters | RegistrationArchivedFilters>>
    const loading = ref(false)
    const currentPage = ref(1)

    const {
      data: registrationsList,
      total: totalRegistrations,
      isLoading: registrationListLoading,
      text,
      queryParams,
      pagination,
      refetch,
      debounceRefetch,
    } = useTableList<RegistrationDoc>({
      dealerId: group.value.dealerId,
      dealerCode: group.value.code,
      pagination: {
        from: 0,
        size: 10,
      },
      queryParams: {
        archived: false,
        accountRegistrationCompleted: false,
        contractsSigned: false,
        deviceHealthChecked: false,
        resolutionStatus: RegistrationResolutionStatus.ActionsNeeded,
      },
      fetchFn: payload => registrationsApi.getRegistrations(payload),
    })
    const loadingFinishRegistration = ref(false)
    const loadingDeletingRegistration = ref(false)
    const headers = ref([
      {
        text: '',
        align: 'center',
        sortable: false,
        value: 'star',
        width: '50px',
        class: 'text-header no-padding-right',
      },
      {
        text: root.$t('honda.registration.list.table.header-date'),
        align: 'left',
        value: 'createdAt',
        class: 'text-header',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-client'),
        align: 'left',
        value: 'account.firstName',
        class: 'text-header',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-model'),
        align: 'left',
        value: 'vehicle.model',
        class: 'text-header',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-matricula'),
        align: 'left',
        value: 'vehicle.registrationNumber',
        class: 'text-header',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-services'),
        align: 'center',
        value: 'products',
        class: 'text-header',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-signature'),
        align: 'center',
        value: 'contractsSigned',
        class: 'text-header',
        width: '85px',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-registration'),
        align: 'center',
        value: 'accountRegistrationCompleted',
        class: 'text-header',
        width: '85px',
        sortable: false,
      },
      {
        text: root.$t('honda.registration.list.table.header-comms'),
        align: 'center',
        value: 'hasComms',
        class: 'text-header no-padding-left',
        width: '85px',
        sortable: false,
      },
      {
        text: root.$t('leads.list.action'),
        align: 'center',
        value: 'actions',
        class: 'text-header',
        sortable: false,
      },
    ])
    const MAX_HOLD_DURATION = 50
    const keyStopper = ref(false)
    const activeKeys = ref<Record<string, any>>({})
    const resultStringPopup = ref('error')
    const dialogResult = ref(false)
    const dialogArchived = ref(false)
    const dialogConfirm = ref(false)
    const selectedToFinish = ref(null) as Ref<RegistrationDoc | null>
    const itemToUnarchive = ref(null) as Ref<RegistrationDoc | null>
    const pendingToDelete = ref(null) as Ref<RegistrationDoc | null>
    const dialogConfirmDelete = ref(false)

    const total = computed(() => {
      return totalRegistrations.value || 0
    })

    const filterListRef = ref<Array<{text: string; value: string}>>(
      props.filterList.map(item => {
        return {
          text: root.$t(`honda.registration.list.btn_${item}`).toString(),
          value: item,
        }
      }),
    )

    watch(filters, () => {
      if (
        filters.value.some(f =>
          [RegistrationArchivedFilters.ArchivedTotal, RegistrationArchivedFilters.ArchivedPending].includes(
            f as RegistrationArchivedFilters,
          ),
        )
      ) {
        queryParams.value.resolutionStatus = filters.value.includes(RegistrationArchivedFilters.ArchivedTotal)
          ? RegistrationResolutionStatus.Finished
          : RegistrationResolutionStatus.ActionsNeeded
        queryParams.value.archived = true
        queryParams.value.accountRegistrationCompleted = undefined
        queryParams.value.contractsSigned = undefined
        queryParams.value.deviceHealthChecked = undefined
      } else {
        queryParams.value.resolutionStatus = RegistrationResolutionStatus.ActionsNeeded
        queryParams.value.archived = false
        queryParams.value.accountRegistrationCompleted = filters.value.includes(RegistrationFilters.PendingRegister)
          ? false
          : undefined
        queryParams.value.contractsSigned = filters.value.includes(RegistrationFilters.PendingSign) ? false : undefined
        queryParams.value.deviceHealthChecked = filters.value.includes(RegistrationFilters.PendingCommunication)
          ? false
          : undefined
      }

      pagination.value.from = 0
      currentPage.value = 1
    })

    function onTextKeydown(e: KeyboardEvent) {
      if (!activeKeys.value[e.code] && e.code !== 'Backspace') {
        activeKeys.value[e.code] = Date.now()
      }

      if (keyStopper.value) {
        e.preventDefault()
        return
      }

      if (Date.now() - activeKeys.value[e.code] > MAX_HOLD_DURATION) {
        e.preventDefault()
        keyStopper.value = true
      }

      if (e.key === 'Enter') {
        e.preventDefault()
        refetch()
      }
    }

    function onTextKeyup({code}: KeyboardEvent) {
      keyStopper.value = false
      delete activeKeys.value[code]
    }

    function openCloseDialogArchive() {
      dialogArchived.value = !dialogArchived.value
      if (itemToUnarchive.value) itemToUnarchive.value = null
    }

    function openCloseDialogConfirmDelete() {
      if (dialogConfirmDelete.value) refetch()
      dialogConfirmDelete.value = !dialogConfirmDelete.value
      pendingToDelete.value = null
    }

    function openCloseDialogConfirm() {
      dialogConfirm.value = !dialogConfirm.value
      if (selectedToFinish.value) selectedToFinish.value = null
    }

    function openCloseDialogResult() {
      if (dialogResult.value) refetch()
      dialogResult.value = !dialogResult.value
    }

    function deleteRegistration() {
      if (!pendingToDelete.value?.contractsSigned) {
        loadingDeletingRegistration.value = true
        coreApi
          .deleteRegistration(pendingToDelete.value?.id)
          .then(() => {
            resultStringPopup.value = 'success_delete'
          })
          .catch((e: any) => {
            resultStringPopup.value = 'error'
            console.log('Error deleting registration: ', e)
          })
          .finally(() => {
            openCloseDialogConfirmDelete()
            dialogResult.value = true
            loadingDeletingRegistration.value = false
          })
      }
    }

    function getProductChips(products: Product[]) {
      if (!products) return {}
      let productChips = [...products]
      if (productChips.includes(Product.InsuranceComprehensive) && productChips.includes(Product.Insurance)) {
        productChips.splice(productChips.indexOf(Product.Insurance), 1)
      }
      if (productChips.includes(Product.HondaPlusGo)) {
        productChips.splice(productChips.indexOf(Product.HondaPlus), 1)
      }
      return productChips.map(product => {
        switch (product) {
          case 'MC':
            return {
              value: product,
              tooltip: 'Mapit Connect',
            }
          case 'HP':
            return {
              value: product,
              tooltip: 'Honda Plus',
            }
          case 'HPG':
            return {
              value: product,
              tooltip: 'Honda Plus Go',
            }
          case 'HM':
            return {
              value: product,
              tooltip: 'Honda Mapit',
            }
          case 'FIN':
            return {
              value: 'HF',
              tooltip: 'Honda Financiación',
            }
          case 'INS':
            return {
              value: 'HS',
              tooltip: 'Honda Seguro básico',
            }
          case 'INS_COM':
            return {
              value: 'HS+',
              tooltip: 'Honda Seguro Ampliado',
            }
        }
      })
    }

    function archiveRegistration(item: RegistrationDoc) {
      if (!loadingFinishRegistration.value) {
        loadingFinishRegistration.value = true
        coreApi
          .archiveRegistration(item.id)
          .then(() => {
            resultStringPopup.value = 'success_archive'
          })
          .catch((e: any) => {
            resultStringPopup.value = 'error'
            console.log('Error mark registration as completed: ', e)
          })
          .finally(() => {
            openCloseDialogConfirm()
            dialogResult.value = true
            loadingFinishRegistration.value = false
          })
      }
    }

    function confirmDeleteRegistration(item: RegistrationDoc) {
      pendingToDelete.value = item
      dialogConfirmDelete.value = true
    }

    function markDocsSigned(item: RegistrationDoc) {
      if (!loading.value) {
        loading.value = true
        coreApi
          .signRegistration(item.id)
          .then(() => {
            resultStringPopup.value = 'success_sign_docs'
            item.contractsSigned = true
          })
          .catch((e: any) => {
            resultStringPopup.value = 'error'
            console.log('Error signing docs: ', e)
          })
          .finally(() => {
            dialogResult.value = true
            loading.value = false
          })
      }
    }

    function getRegistrationContracts(item: RegistrationDoc) {
      if (!loading.value) {
        loading.value = true
        registrationsApi
          .getRegistrationContracts(item.id)
          .then(({data}) => {
            let docs: any[] = []
            data.forEach((contract: any) => {
              if (contract.s3Key) {
                docs.push({url: contract.documentUrl, name: `${item.vehicle.vin}_${contract.name}.pdf`})
              }
            })
            downloadSignedUrlFiles(docs)
            resultStringPopup.value = 'success_download'
          })
          .catch(e => {
            resultStringPopup.value = 'error'
            console.log('Error download contract files: ', e)
          })
          .finally(() => {
            dialogResult.value = true
            loading.value = false
          })
      }
    }

    function sendEmailSignDocs(item: RegistrationDoc) {
      if (!loading.value) {
        loading.value = true
        coreApi
          .sendEmailContractHondaPlus(item.id)
          .then(() => {
            resultStringPopup.value = 'success_send_docs'
          })
          .catch((e: any) => {
            resultStringPopup.value = 'error'
            console.log('Error sending email: ', e)
          })
          .finally(() => {
            dialogResult.value = true
            loading.value = false
          })
      }
    }

    function sendEmailRegisterApp(item: RegistrationDoc) {
      if (!loading.value) {
        loading.value = true
        coreApi
          .sendEmailDownloadApp(item)
          .then(() => {
            resultStringPopup.value = 'success_send_invite'
          })
          .catch((e: any) => {
            resultStringPopup.value = 'error'
            console.log('Error sending email: ', e)
          })
          .finally(() => {
            dialogResult.value = true
            loading.value = false
          })
      }
    }

    function confirmFinish(item: RegistrationDoc) {
      dialogConfirm.value = true
      selectedToFinish.value = item
    }

    function confirmUnarchive(item: RegistrationDoc) {
      dialogArchived.value = true
      itemToUnarchive.value = item
    }

    function hasHondaMapit(item: RegistrationDoc) {
      return item.products.includes(Product.HondaMapit) || item.products.includes(Product.MapitConnect)
    }

    function hasHondaPlusOrInsurance(item: RegistrationDoc) {
      return (
        item.products.includes(Product.HondaPlus) ||
        item.products.includes(Product.Insurance) ||
        item.products.includes(Product.HondaPlusGo)
      )
    }

    function hasComms(item: RegistrationDoc) {
      return hasHondaMapit(item) && item.deviceHealthChecked !== undefined && item.deviceHealthChecked
    }

    function trimName(name: string, max: number) {
      return name.length >= max ? name.substring(0, max) + '...' : name
    }

    function getFullName(item: RegistrationDoc) {
      return `${item.account.firstName} ${item.account.lastName}`
    }

    function isRecent(item: RegistrationDoc) {
      const a = moment(item.createdAt)
      const b = moment(new Date()).add(-1, 'hours')
      return a.isAfter(b)
    }

    function onPageChange(page: any) {
      pagination.value.from = (page - 1) * (pagination.value.size || 10)
      refetch()
    }

    function onItemsPerPageChange(itemsPerPage: any) {
      pagination.value.size = itemsPerPage
      refetch()
    }

    function onSortByChange(column?: string) {
      switch (column) {
        case 'createdAt':
          pagination.value.column = 'createDate'
          break
        case 'account.firstName':
          pagination.value.column = 'user'
          break
        case 'vehicle.model':
          pagination.value.column = 'model'
          break
        case 'vehicle.registrationNumber':
          pagination.value.column = 'registrationNumber'
          break
        case 'products':
          pagination.value.column = 'products'
          break
        case 'contractsSigned':
          pagination.value.column = 'contractSigned'
          break
        case 'accountRegistrationCompleted':
          pagination.value.column = 'accountRegistrationCompleted'
          break
        case 'hasComms':
          pagination.value.column = 'deviceHealthChecked'
          break
        default:
          pagination.value.column = 'createDate'
      }
      pagination.value.sort = 'asc'
      debounceRefetch()
    }

    function onSortDescChange(desc: Array<boolean | undefined>) {
      const [isDesc] = desc
      pagination.value.sort = 'desc'

      if (desc.length > 0 && !isDesc) {
        pagination.value.sort = 'asc'
      }
      debounceRefetch()
    }

    function getRowClass() {
      return 'registrations-table__row'
    }

    function getDocumentNumber(item: RegistrationDoc) {
      //@ts-ignore
      return item.account.document ? item.account.document.number : ''
    }

    function unarchiveRegistration(item: RegistrationDoc) {
      if (!loadingFinishRegistration.value) {
        loadingFinishRegistration.value = true
        registrationsApi
          .unarchiveRegistration(item.id)
          .then(() => {
            resultStringPopup.value = 'success_unarchive'
          })
          .catch((e: any) => {
            resultStringPopup.value = 'error'
            console.log('Error unarchiving registration: ', e)
          })
          .finally(() => {
            openCloseDialogArchive()
            dialogResult.value = true
            loadingFinishRegistration.value = false
          })
      }
    }

    return {
      group,
      loading,
      registrationsList,
      total,
      registrationListLoading,
      text,
      queryParams,
      pagination,
      refetch,
      debounceRefetch,
      headers,
      filterListRef,
      dialogArchived,
      dialogResult,
      resultStringPopup,
      selectedToFinish,
      dialogConfirm,
      dialogConfirmDelete,
      loadingFinishRegistration,
      loadingDeletingRegistration,
      filters,
      currentPage,
      itemToUnarchive,
      onTextKeydown,
      onTextKeyup,
      unarchiveRegistration,
      onPageChange,
      onItemsPerPageChange,
      onSortByChange,
      onSortDescChange,
      getRowClass,
      getProductChips,
      isRecent,
      getFullName,
      trimName,
      hasHondaMapit,
      hasHondaPlusOrInsurance,
      confirmFinish,
      sendEmailRegisterApp,
      sendEmailSignDocs,
      getRegistrationContracts,
      markDocsSigned,
      confirmDeleteRegistration,
      openCloseDialogArchive,
      openCloseDialogResult,
      openCloseDialogConfirm,
      openCloseDialogConfirmDelete,
      finishRegistration: archiveRegistration,
      deleteRegistration,
      hasComms,
      getDocumentNumber,
      generateUUID,
      confirmUnarchive,
    }
  },
})
</script>
<style lang="scss">
#offer-status-filter {
  .v-list-item:nth-child(3) {
    border-bottom: 1px solid #dddddd;
  }
}
</style>
<style lang="scss" scoped>
#registrations-table-list {
  font: normal normal normal 15px/22px 'ProximaNovaRegular';

  .lbl-bold {
    font: normal normal normal 15px/22px 'ProximaNovaSemibold' !important;
  }

  .text-start,
  .text-center {
    padding: 0;
  }

  .text-header {
    letter-spacing: 0;
    color: var(--v-gray-lighten5);
    text-align: left;
    font: normal normal 600 13px/15px 'ProximaNovaExCnSBold';
    text-transform: uppercase;
    opacity: 1;
  }

  .text-item {
    letter-spacing: 0;
    text-align: left;
    font: normal normal normal 15px/22px 'ProximaNovaRegular';
    color: #000000;
    opacity: 1;
  }

  .icon-mapit {
    text-align: center;
    letter-spacing: 0;
    color: var(--v-gray-base);
    opacity: 1;
  }

  .v-data-table__empty-wrapper {
    font: normal normal normal 15px/22px 'ProximaNovaRegular';
    letter-spacing: 0;
    color: black;
    opacity: 1;
  }

  .customer {
    .customer-header {
      font: normal normal normal 15px/22px 'ProximaNovaSemibold' !important;
      color: var(--v-gray-darken4);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 !important;
      margin: 0 !important;
      line-height: 26px;
    }

    .customer-phone,
    .customer-email {
      font-family: 'ProximaNovaRegular', sans-serif;
      color: var(--v-gray-lighten5);
      font-size: 14px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 !important;
      margin: 0 !important;
      line-height: 20px;
    }
  }
}

.registrations-search-btn {
  border-radius: 3px;
  margin-left: 8px;

  &:focus {
    outline: none !important;
    border: none !important;
  }
}

.no-shadow {
  box-shadow: none !important;
}

.btn-text {
  font: normal normal normal 15px/22px 'ProximaNovaRegular';
  text-transform: initial;
}

.aux-btn {
  color: rgba(0, 0, 0, 0.35) !important;
  border-color: rgba(0, 0, 0, 0.27) !important;

  i {
    font-size: 20px;
  }
}

.list-menu-actions {
  min-width: 200px !important;

  .v-list-item {
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
    }

    &__title {
      font-size: 14px;
      user-select: none;
    }
  }

  .v-list-item__title {
    font-size: 14px;
  }
}

.service-chip {
  border: 1px solid rgba(0, 0, 0, 0.27) !important;
}
</style>
